// Presentation-only adapters. Financial totals always come from the canonical
// visit snapshot; filtering and grouping must never recalculate them.
import { uniqueDocuments, KIND_LABELS, STATUS_LABELS, PAYMENT_LABELS } from '../accounting/workshopFinanceModel';

export const EVENT_TYPES = [
    { value: 'all', label: 'كل السجل' },
    { value: 'visit_summary', label: 'ملخصات الزيارات' },
    { value: 'expense', label: 'مصروفات مستقلة', document: true },
    { value: 'income', label: 'إيرادات مستقلة', document: true },
    { value: 'payroll', label: 'رواتب', document: true, adminOnly: true },
    { value: 'visit_received', label: 'الاستقبال' },
    { value: 'service_item', label: 'الخدمات' },
    { value: 'part_item', label: 'القطع' },
    { value: 'supplier_purchase', label: 'المشتريات' },
    { value: 'customer_payment', label: 'الدفعات' },
    { value: 'visit_finalized', label: 'الاعتماد' },
    { value: 'vehicle_delivered', label: 'التسليم' },
];

const present = (value) => value !== null && value !== undefined && String(value).trim() !== '';
export const textValue = (...values) => String(values.find(present) ?? '').trim();
export const numberValue = (...values) => {
    for (const value of values) {
        if ((typeof value !== 'number' && typeof value !== 'string') || !present(value)) continue;
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return null;
};
const booleanValue = (...values) => values.find((value) => value === true || value === false) ?? null;
const obj = (value) => value && typeof value === 'object' && !Array.isArray(value) ? value : {};

export const formatMoney = (value) => value === null || value === undefined ? 'غير متاح' : new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(value);

export const formatDate = (value) => {
    if (!value || Number.isNaN(new Date(value).getTime())) return 'التاريخ غير مسجل';
    return new Intl.DateTimeFormat('ar-SA-u-ca-gregory-nu-latn', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(new Date(value));
};

export const normalizeVehicle = (value, raw = {}) => {
    const vehicle = obj(value);
    const make = textValue(vehicle.make, vehicle.brand);
    const model = textValue(vehicle.model);
    return {
        id: textValue(vehicle.id, raw.vehicleId, raw.vehicle_id),
        customerName: textValue(vehicle.customerName, vehicle.customer_name, raw.customerName, raw.customer_name),
        plateNumber: textValue(vehicle.plateNumber, vehicle.plate_number, raw.plateNumber, raw.plate_number),
        fileNumber: textValue(vehicle.fileNumber, vehicle.file_number, raw.fileNumber, raw.file_number),
        make, model, year: textValue(vehicle.year),
        name: textValue(vehicle.name, [make, model].filter(Boolean).join(' ')),
    };
};

export const normalizeSource = (value, raw = {}) => {
    const source = obj(value);
    return {
        type: textValue(source.type, source.table, source.kind, raw.sourceTable, raw.source_table),
        id: textValue(source.id, source.recordId, source.record_id, raw.sourceRecordId, raw.source_record_id),
        label: textValue(source.label, raw.sourceLabel, raw.source_label),
    };
};

export const actorLabel = (value, fallback = '') => {
    const actor = obj(value);
    if (['unrecorded', 'unknown'].includes(textValue(actor.kind).toLowerCase())) return 'غير مسجل';
    const name = textValue(actor.label, actor.displayName, actor.name, actor.username, actor.recordedAs, fallback);
    return !name || ['vehicle_visit_api', 'vehicle_details', 'system', 'system_seed', 'تسجيل نظامي', 'غير محفوظ في المصدر'].includes(name.toLowerCase()) ? 'غير مسجل' : name;
};

export const originLabel = (value) => {
    const origin = obj(value);
    // A source table or payment method is not evidence of an entry channel.
    if (!origin.channel || ['unknown', 'unrecorded'].includes(origin.channel)) return 'غير مسجل';
    return textValue(origin.label, 'غير مسجل');
};

export const bearerKind = (item) => {
    const explicit = textValue(item.costBearer, item.cost_bearer).toLowerCase();
    if (['customer', 'client'].includes(explicit)) return 'customer';
    if (['workshop', 'shop'].includes(explicit)) return 'workshop';
    if (explicit === 'unknown') return 'unknown';
    const billable = booleanValue(item.customerBillable, item.customer_billable);
    return billable === true ? 'customer' : billable === false ? 'workshop' : 'unknown';
};
export const bearerLabel = (item) => ({ customer: 'العميل', workshop: 'الورشة', unknown: 'غير محددة' }[bearerKind(item)]);
export const normalizeItem = (value) => {
    const item = obj(value);
    return {
        ...item,
        id: textValue(item.id),
        itemType: textValue(item.itemType, item.item_type),
        name: textValue(item.name, item.description),
        quantity: numberValue(item.quantity),
        unitPrice: numberValue(item.unitPrice, item.unit_price),
        lineTotal: numberValue(item.lineTotal, item.line_total, item.amount),
        customerBillable: booleanValue(item.customerBillable, item.customer_billable),
        costBearer: textValue(item.costBearer, item.cost_bearer),
        supplierName: textValue(item.supplierName, item.supplier_name),
        supplierSettlementStatus: textValue(item.supplierSettlementStatus, item.supplier_settlement_status),
        supplierPaymentMethod: textValue(item.supplierPaymentMethod, item.supplier_payment_method),
        source: normalizeSource(item.source),
    };
};

export const settlement = (value) => {
    const state = textValue(value).toLowerCase();
    if (['paid', 'settled', 'مدفوع', 'مسدد', 'مسدّد', 'تم السداد'].includes(state)) return { label: 'مسدّد لصالح المورد', known: true, settled: true };
    if (['credit', 'pending', 'unpaid', 'بانتظار السداد', 'غير مدفوع', 'غير مسدد', 'مستحق'].includes(state)) return { label: 'مستحق لصالح المورد', known: true, settled: false };
    return { label: 'السداد غير محدد', known: false, settled: false };
};

export const methodLabel = (value) => ({ cash: 'نقدي', pos: 'نقاط بيع', card: 'بطاقة', bank_transfer: 'تحويل بنكي', transfer: 'تحويل بنكي' }[value] || value || 'غير مسجلة');

export const normalizeEvent = (value) => {
    const raw = obj(value);
    const source = normalizeSource(raw.source, raw);
    const type = textValue(raw.eventType, raw.event_type, 'visit_update').toLowerCase();
    // Never invent an array-index identity for financial source rows.
    const id = textValue(raw.id, source.id ? `${type}:${source.id}` : '');
    if (!id) return null;
    const payment = obj(raw.payment);
    const visit = obj(raw.visit);
    const item = normalizeItem(raw.item);
    return {
        id, eventType: type, source,
        vehicle: normalizeVehicle(raw.vehicle, raw),
        visit: { id: textValue(visit.id, raw.visitId, raw.visit_id), number: textValue(visit.number, visit.visitNumber, visit.visit_number), status: textValue(visit.status) },
        occurredAt: textValue(raw.occurredAt, raw.occurred_at, raw.createdAt, raw.created_at),
        amount: numberValue(raw.amount, raw.total, item.lineTotal, payment.amount),
        detail: textValue(raw.detail, raw.description),
        item,
        payment: {
            method: textValue(payment.method, payment.paymentMethod, payment.payment_method),
            reference: textValue(payment.reference, payment.referenceNumber, payment.reference_number),
            arApplied: numberValue(payment.arApplied, payment.ar_applied),
            unappliedAdvance: numberValue(payment.unappliedAdvance, payment.unapplied_advance),
        },
        actor: actorLabel(raw.actor, textValue(raw.recordedBy, raw.createdBy, payment.createdBy, visit.finalizedBy)),
        origin: originLabel(raw.origin),
    };
};

const summaryMoneyFields = ['amount', 'serviceTotal', 'partsTotal', 'customerPurchaseTotal', 'workshopItemsTotal', 'supplierPurchaseTotal', 'collectedTotal', 'arApplied', 'advance', 'remaining'];
export const normalizeSummary = (value) => {
    const raw = obj(value);
    const visit = obj(raw.visit);
    const visitId = textValue(raw.visitId, visit.id);
    if (!visitId) return null;
    const items = Array.isArray(raw.items) ? raw.items.map(normalizeItem) : [];
    const ids = items.map((item) => item.id);
    const itemsComplete = raw.itemsComplete === true && ids.every(Boolean) && new Set(ids).size === ids.length;
    const verified = raw.snapshot?.status === 'verified' && itemsComplete;
    const totals = Object.fromEntries(summaryMoneyFields.map((key) => [key, verified ? numberValue(raw[key]) : null]));
    return {
        id: textValue(raw.id, `visit-summary:${visitId}`),
        visitId,
        vehicle: normalizeVehicle(raw.vehicle),
        visit: { id: visitId, number: textValue(visit.number), status: textValue(visit.status) },
        financialState: textValue(raw.financialState, visit.financialState, 'unavailable'),
        ...totals,
        items, itemsComplete, verified,
        updatedAt: textValue(raw.updatedAt),
        source: normalizeSource(raw.source),
        origin: originLabel(raw.origin),
        finalizedBy: actorLabel(raw.finalizedBy),
        coveredEventIds: new Set(itemsComplete && Array.isArray(raw.coveredEventIds) ? raw.coveredEventIds.map(String) : []),
    };
};

export const normalizeLog = (payload) => {
    const rawEvents = Array.isArray(payload) ? payload : payload?.events;
    const events = [];
    const eventIds = new Set();
    let omittedRecords = false;
    (Array.isArray(rawEvents) ? rawEvents : []).forEach((raw) => {
        const event = normalizeEvent(raw);
        if (!event) { omittedRecords = true; return; }
        if (!eventIds.has(event.id)) { eventIds.add(event.id); events.push(event); }
    });
    const summaries = [];
    const summaryVisits = new Set();
    (Array.isArray(payload?.visitSummaries) ? payload.visitSummaries : []).forEach((raw) => {
        const summary = normalizeSummary(raw);
        if (!summary) { omittedRecords = true; return; }
        if (!summaryVisits.has(summary.visitId)) { summaryVisits.add(summary.visitId); summaries.push(summary); }
    });
    return {
        events, summaries,
        coverage: {
            truncated: payload?.truncated === true,
            hasMoreVisits: payload?.hasMoreVisits === true,
            snapshotUnavailable: payload?.summarySnapshot?.status === 'unavailable',
            omittedRecords,
        },
    };
};

export const eventPresentation = (event) => {
    const type = event.eventType;
    if (type === 'visit_received') return { title: 'استقبال مركبة', tone: 'operational', icon: 'car', cue: 'لا أثر مالي', subject: 'تسجيل استقبال الزيارة' };
    if (type === 'vehicle_delivered') return { title: 'تم التسليم', tone: 'operational', icon: 'archive', cue: 'لا أثر مالي', subject: 'تسليم وأرشفة الزيارة' };
    if (type === 'visit_finalized') return { title: 'اعتماد الزيارة', tone: 'finalization', icon: 'approval', cue: 'معتمدة', subject: 'إجمالي الاعتماد المسجل' };
    if (type === 'supplier_purchase') return { title: 'شراء مورد', tone: 'supplier', icon: 'package', cue: settlement(event.item.supplierSettlementStatus).label, subject: event.item.name || 'شراء مرتبط بالزيارة' };
    if (['service_item', 'part_item'].includes(type)) {
        const bearer = bearerKind(event.item);
        return {
            title: type === 'part_item' ? 'قطعة مضافة' : 'خدمة مسجلة',
            tone: bearer === 'customer' ? 'customer' : bearer === 'workshop' ? 'workshop' : 'operational',
            icon: type === 'part_item' ? 'package' : 'wrench',
            cue: bearer === 'customer' ? 'ضمن الزيارة' : bearer === 'workshop' ? 'على الورشة' : 'جهة غير محددة',
            subject: event.item.name || 'البند غير مسمى',
        };
    }
    if (type === 'customer_payment') {
        const applied = event.payment.arApplied;
        const advance = event.payment.unappliedAdvance;
        const visitNumber = textValue(event.visit?.number);
        const visitLabel = visitNumber ? `زيارة رقم ${visitNumber}` : 'الزيارة المرتبطة';
        // Both canonical allocation components must agree with this payment row
        // before the UI names it as settlement or customer advance.
        const known = applied !== null && advance !== null && event.amount !== null
            && applied >= 0 && advance >= 0
            && Math.abs(event.amount - applied - advance) <= 0.01;
        const mixed = known && applied > 0 && advance > 0;
        if (known && applied > 0 && advance === 0) {
            return {
                title: `سداد ${visitLabel}`, icon: 'payment', tone: 'collection',
                cue: 'مخصص حاليًا للسداد', subject: methodLabel(event.payment.method),
            };
        }
        if (known && advance > 0 && applied === 0) {
            return {
                title: `دفعة مقدمة — ${visitLabel}`, icon: 'payment', tone: 'advance',
                cue: 'مقدم لصالح العميل', subject: methodLabel(event.payment.method),
            };
        }
        return {
            title: `دفعة ${visitLabel}`, icon: 'payment',
            tone: !known || (applied === 0 && advance === 0) ? 'operational' : applied > 0 ? 'collection' : 'advance',
            cue: !known ? 'التوزيع غير محدد' : mixed ? 'تحصيل ومقدم' : applied > 0 ? 'لصالح الورشة' : advance > 0 ? 'مقدم لصالح العميل' : 'دفعة مسجلة',
            subject: methodLabel(event.payment.method),
        };
    }
    return { title: 'حدث مسجل', tone: 'operational', icon: 'record', cue: 'التفاصيل المتاحة', subject: event.detail };
};

const vehicleSearch = (vehicle) => Object.values(vehicle).join(' ');
export const normalizeSearch = (value) => String(value || '').toLocaleLowerCase('ar-SA')
    .replace(/[\u064B-\u065F\u0670\u0640]/g, '').replace(/[أإآ]/g, 'ا')
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit))).trim();
const matchesSearch = (text, query) => !query || query.split(/\s+/).every((word) => normalizeSearch(text).includes(word));
export const eventSearch = (event) => [vehicleSearch(event.vehicle), event.visit.number, event.detail, event.item.name, event.item.supplierName, event.source.id, event.payment.reference, event.actor, event.origin, eventPresentation(event).title].join(' ').toLocaleLowerCase('ar-SA');
export const summarySearch = (summary) => [vehicleSearch(summary.vehicle), summary.visit.number, summary.source.id, ...summary.items.flatMap((item) => [
    item.name, item.supplierName, item.source.id, actorLabel(item.actor), originLabel(item.origin),
    { service: 'خدمة مسجلة', part: 'قطعة مضافة', supplier: 'شراء مورد', supplier_purchase: 'شراء مورد' }[item.itemType],
])].join(' ').toLocaleLowerCase('ar-SA');
const eventItemType = { service_item: 'service', part_item: 'part', supplier_purchase: 'supplier' };
export const isSupplierItem = (item) => ['supplier', 'supplier_purchase'].includes(item.itemType);

export const documentSearch = (document) => [document.description, document.number, document.id,
    document.customer?.name, document.supplier?.name, document.payee_name,
    document.link_type === 'vehicle' ? vehicleSearch(document.vehicle || {}) : '',
    document.payment_reference, KIND_LABELS[document.kind], STATUS_LABELS[document.status],
    STATUS_LABELS[document.settlement_status], PAYMENT_LABELS[document.payment_method],
    document.kind === 'income' ? 'إيرادات مستقلة' : 'مصروفات مستقلة',
    document.kind === 'payroll' ? 'راتب رواتب' : '',
].join(' ');

export const selectLog = ({ events, summaries }, { search = '', selectedType = 'all', vehicleId = '', documents = [], canViewPayroll = false } = {}) => {
    const query = normalizeSearch(search);
    const scopedSummaries = summaries.filter((summary) => !vehicleId || summary.vehicle.id === vehicleId);
    const scopedEvents = events.filter((event) => !vehicleId || event.vehicle.id === vehicleId);
    const searchableEvents = new Map(scopedEvents.map((event) => [event.id, eventSearch(event)]));
    const shownSummaries = scopedSummaries.filter((summary) => {
        if (!summary.itemsComplete) return false;
        if (!['all', 'visit_summary'].includes(selectedType)) {
            const kind = eventItemType[selectedType];
            if (!kind || !summary.items.some((item) => kind === 'supplier' ? isSupplierItem(item) : item.itemType === kind)) return false;
        }
        // Search and type must match the same item, not unrelated rows of a visit.
        // Context (customer/vehicle/visit) can still select its complete summary.
        const kind = eventItemType[selectedType];
        const matchingItems = kind ? summary.items.filter((item) => kind === 'supplier' ? isSupplierItem(item) : item.itemType === kind) : summary.items;
        return matchesSearch(summarySearch({ ...summary, items: [] }), query)
            || matchingItems.some((item) => matchesSearch(summarySearch({ ...summary, items: [item] }), query))
            || scopedEvents.some((event) => summary.coveredEventIds.has(event.id)
                && (!kind || event.eventType === selectedType)
                && matchesSearch(searchableEvents.get(event.id), query));
    });
    // Suppress item cards only behind a displayed complete summary. A search
    // matching a covered event's actor/title must retain that whole summary.
    const suppressed = new Set(shownSummaries.flatMap((summary) => Array.from(summary.coveredEventIds)));
    const shownEvents = scopedEvents.filter((event) => !(suppressed.has(event.id) && ['service_item', 'part_item', 'supplier_purchase'].includes(event.eventType))
        && (selectedType === 'all' || event.eventType === selectedType)
        && matchesSearch(eventSearch(event), query));
    const shownDocuments = uniqueDocuments(documents).filter((document) =>
        (document.kind !== 'payroll' || canViewPayroll)
        && (!vehicleId || (document.link_type === 'vehicle' && document.vehicle?.id === vehicleId))
        && (selectedType === 'all' || selectedType === document.kind)
        && matchesSearch(documentSearch(document), query));
    const cards = [
        ...shownSummaries.map((summary) => ({ key: summary.id, kind: 'summary', value: summary, at: summary.updatedAt })),
        ...shownEvents.map((event) => ({ key: `event:${event.id}`, kind: 'event', value: event, at: event.occurredAt })),
        ...shownDocuments.map((document) => ({ key: `document:${document.id}`, kind: 'document', value: document, at: document.created_at || document.entry_date })),
    ].sort((a, b) => (Date.parse(b.at) || 0) - (Date.parse(a.at) || 0) || a.key.localeCompare(b.key));
    return { cards, summaryCount: shownSummaries.length, eventCount: shownEvents.length, documentCount: shownDocuments.length };
};
